import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Visibility, VisibilityOff, AdminPanelSettings, Person, Lock } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from 'notistack';

const Login = ({ isAdmin = false }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAdminPasswordDialog, setShowAdminPasswordDialog] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPasswordError, setAdminPasswordError] = useState('');
  const [tempUser, setTempUser] = useState(null);
  
  const navigate = useNavigate();
  const { login } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      // Check if user is admin/recruiter and needs admin password verification
      if ((result.user?.role === 'admin' || result.user?.role === 'recruiter') && isAdmin) {
        setTempUser(result.user);
        setShowAdminPasswordDialog(true);
        setLoading(false);
        return;
      }
      
      enqueueSnackbar('Login successful!', { variant: 'success' });
      // Navigate based on user role
      if (result.user?.role === 'admin' || result.user?.role === 'recruiter') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.message || 'Login failed. Please try again.');
    }
    
    setLoading(false);
  };

  const handleAdminPasswordSubmit = () => {
    if (adminPassword === 'RISO@2025') {
      setShowAdminPasswordDialog(false);
      setAdminPassword('');
      setAdminPasswordError('');
      // Set admin password verification in session storage
      sessionStorage.setItem('adminPasswordVerified', 'true');
      enqueueSnackbar('Admin access granted!', { variant: 'success' });
      navigate('/admin');
    } else {
      setAdminPasswordError('Incorrect admin password');
    }
  };

  const handleAdminPasswordCancel = () => {
    setShowAdminPasswordDialog(false);
    setAdminPassword('');
    setAdminPasswordError('');
    // Logout the user since admin password was incorrect
    window.location.reload();
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: isAdmin ? '#1a237e' : '#2196f3',
                mb: 2,
                mx: 'auto'
              }}
            >
              {isAdmin ? (
                <AdminPanelSettings sx={{ color: 'white', fontSize: 30 }} />
              ) : (
                <Person sx={{ color: 'white', fontSize: 30 }} />
              )}
            </Box>
            <Typography component="h1" variant="h4" gutterBottom>
              {isAdmin ? 'Admin Login' : 'Welcome Back'}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {isAdmin ? 'Sign in to administrator portal' : 'Sign in to your account'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                background: isAdmin ? 'linear-gradient(135deg, #1a237e, #3949ab)' : 'linear-gradient(135deg, #2196f3, #1976d2)',
                '&:hover': {
                  background: isAdmin ? 'linear-gradient(135deg, #0d47a1, #1a237e)' : 'linear-gradient(135deg, #1976d2, #1565c0)'
                }
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link
                to={isAdmin ? '/admin-register' : '/register'}
                style={{
                  textDecoration: 'none',
                  color: isAdmin ? '#1a237e' : '#2196f3',
                  fontWeight: 'medium'
                }}
              >
                {isAdmin ? "Don't have an admin account? Register" : "Don't have an account? Sign Up"}
              </Link>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                to="/forgot-password"
                style={{
                  textDecoration: 'none',
                  color: isAdmin ? '#1a237e' : '#2196f3',
                  fontSize: '0.875rem'
                }}
              >
                Forgot password?
              </Link>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link
                to={isAdmin ? '/login' : '/admin-login'}
                style={{
                  textDecoration: 'none',
                  color: isAdmin ? '#1a237e' : '#2196f3',
                  fontSize: '0.875rem'
                }}
              >
                {isAdmin ? 'Login as Candidate' : 'Login as Administrator'}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Dialog open={showAdminPasswordDialog} onClose={handleAdminPasswordCancel}>
        <DialogTitle>Admin Password Verification</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Lock sx={{ mr: 1 }} />
            <Typography variant="body1">
              Please enter the admin password to access the administrator portal.
            </Typography>
          </Box>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Admin Password"
            type="password"
            id="admin-password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            error={!!adminPasswordError}
            helperText={adminPasswordError}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle admin password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAdminPasswordCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleAdminPasswordSubmit} variant="contained" color="primary">
            Verify
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login;
